import { FC, RefObject, useEffect, useRef, useState } from 'react';

import { Icon } from '@pp/core/src/components/icons/icon';
import { Icons } from '@pp/core/src/components/icons/iconNames';
import { CurrencyFormatter } from '@pp/core/src/formatters/CurrencyFormatter';
import { useMedia } from '@pp/core/src/hooks/useMedia';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { ILimit } from '@pp/core/src/store/slices/bets/types';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';
import { roundNumber } from '@pp/core/src/utils/number';

import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';

import styles from './VirtualKeypad.module.css';

export interface VirtualKeypadProps {
  betCode: number;
  betLabel: string;
  minLimits: number;
  maxLimits: number;
  currency?: string;
  currentLanguage: string;
  hideCurrency?: boolean;
  betInputValue: string;
  betInputAmount: number;
  handleQuickBet: (betId: number, priceValue: string) => void;
  handleDispatch: (updatedPriceValue: string) => void;
  showFormatter: boolean;
  formatter?: string;
}

export const VirtualKeypad: FC<VirtualKeypadProps> = ({
  betCode,
  betLabel,
  minLimits,
  maxLimits,
  currency = '',
  currentLanguage,
  hideCurrency = false,
  betInputAmount,
  handleQuickBet,
  showFormatter,
  formatter = '',
}) => {
  const keypadNumbers = [1, 2, 3, 'X', 4, 5, 6, '.', 7, 8, 9, 0];
  const [updatedPriceValue, setUpdatedPriceValue] = useState(minLimits.toString());
  const isWide = useMedia('(min-width: 1024px)');
  const regExpr = /^\d+(\.\d{0,2})?$/;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isKeypadOpened, setIsKeypadOpened] = useState(false);
  const remainingBalance = useHighflyerStateSlice.balance();
  const [isExtraValue, setIsExtraValue] = useState(false);

  const priceValue = (priceValue: string | number, lastPriceValue: string) => {
    if (isEditing) {
      setIsEditing(false);
      setIsExtraValue(false);
      setUpdatedPriceValue(priceValue.toString());
    } else {
      if (priceValue === 'X') {
        setUpdatedPriceValue((prev) => prev.slice(0, prev.length - 1));
        setIsExtraValue(false);
        setIsKeypadOpened(false);
      } else {
        setUpdatedPriceValue((prev) => {
          let updatedVal;
          if (isKeypadOpened) {
            updatedVal = priceValue;
            setIsKeypadOpened(false);
            setIsExtraValue(false);
            return updatedVal.toString();
          }
          updatedVal = prev + priceValue;
          if (updatedVal === '.') {
            return updatedVal;
          } else {
            updatedVal = updatedVal.replace(/^(.*?\.)(.*)$/g, (x, y, z) => y + z.replace(/\./g, ''));
            let formattedVal = updatedVal;
            const formatValArr: any[] = formattedVal.split('.');
            if (formatValArr[0]?.length === 0) {
              if (formatValArr[1]?.length > 2) {
                return lastPriceValue;
              }
              formattedVal = parseFloat(updatedVal).toString();
            }

            if (regExpr.test(formattedVal)) {
              setIsExtraValue(false);
              return updatedVal;
            } else {
              setIsExtraValue(true);
              return lastPriceValue;
            }
          }
        });
      }
    }
  };

  useEffect(() => {
    setUpdatedPriceValue(betInputAmount.toString());
    setIsKeypadOpened(true);
  }, []);

  const clearPrice = () => {
    setUpdatedPriceValue('');
  };

  const handleClose = () => {
    handleQuickBet(betCode, betInputAmount.toString());
  };

  const checkCurrencyFormat = () => {
    if (showFormatter) {
      if (Number(updatedPriceValue) > maxLimits) {
        return maxLimits + formatter;
      } else if (Number(updatedPriceValue) < minLimits || updatedPriceValue === '.') {
        return minLimits + formatter;
      }
    } else {
      if (Number(updatedPriceValue) < minLimits || updatedPriceValue === '.') {
        return CurrencyFormatter.format(minLimits, currentLanguage, { currency: currency, hideCurrency });
      }
      if (remainingBalance < maxLimits) {
        return CurrencyFormatter.format(remainingBalance, currentLanguage, { currency: currency, hideCurrency });
      } else if (Number(updatedPriceValue) > maxLimits) {
        return CurrencyFormatter.format(maxLimits, currentLanguage, { currency: currency, hideCurrency });
      }
    }
  };

  const onFocus = () => {
    inputRef?.current?.select();
    setIsEditing(true);
  };

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current?.addEventListener('focus', onFocus);
    }
    return () => {
      inputRef.current?.removeEventListener('focus', onFocus);
    };
  }, [inputRef, onFocus]);

  const handleApply = () => {
    if (Number(updatedPriceValue) < minLimits || updatedPriceValue === '.') {
      handleQuickBet(betCode, minLimits.toString());
    } else if (Number(updatedPriceValue) > maxLimits && !showFormatter && remainingBalance < maxLimits) {
      handleQuickBet(betCode, remainingBalance.toString());
    } else if (Number(updatedPriceValue) > maxLimits) {
      handleQuickBet(betCode, maxLimits.toString());
    } else if (
      !showFormatter &&
      Number(updatedPriceValue) > remainingBalance &&
      Number(updatedPriceValue) < maxLimits
    ) {
      handleQuickBet(betCode, remainingBalance.toString());
    } else {
      handleQuickBet(betCode, updatedPriceValue.toString());
    }
  };

  return (
    !isWide && (
      <div className={styles.keypadWrapperContainer}>
        <div className={styles.keypadWrapper}>
          <div className={styles.betHeader}>
            <div className={styles.betlabel}>
              <L10nText tk={betLabel} />
            </div>
            <div className={styles.closeIcon} onClick={() => handleClose()}>
              <Icon icon={Icons.Close} color="white"></Icon>
            </div>
          </div>
          <div className={styles.keypadContainer}>
            <div className={styles.inputkeypad}>
              <KeypadInput
                betInputValue={updatedPriceValue}
                minLimits={minLimits}
                maxLimits={maxLimits}
                clearPrice={clearPrice}
                showFormatter={showFormatter}
                formatter={formatter}
                inputRef={inputRef}
                remainingBalance={remainingBalance}
                isExtraValue={isExtraValue}
              />
              <TableLimts
                tableLimits={{ min: minLimits, max: maxLimits }}
                currency={currency}
                hideCurrency={hideCurrency}
                currentLanguage={currentLanguage}
                formatter={formatter}
              />
            </div>

            {keypadNumbers &&
              keypadNumbers.map((value: number | string) => (
                <div key={value} className={styles.keypad} onClick={() => priceValue(value, updatedPriceValue)}>
                  {value === 'X' ? subtractIcon() : value}
                </div>
              ))}

            <button className={styles.submitBtn} onClick={() => handleApply()}>
              <label className={styles.applyBtn}>
                {parseFloat(updatedPriceValue) >= minLimits &&
                parseFloat(updatedPriceValue) <= maxLimits &&
                parseFloat(updatedPriceValue) <= remainingBalance &&
                updatedPriceValue !== '.' ? (
                    <L10nText tk="APPLY_CAPS" />
                  ) : (
                    checkCurrencyFormat()
                  )}
              </label>
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export const subtractIcon = () => {
  return <div className={styles.subtractIcon}></div>;
};

const KeypadInput: FC<{
  betInputValue: string;
  minLimits: number;
  maxLimits: number;
  clearPrice: () => void;
  showFormatter: boolean;
  formatter: string;
  inputRef?: RefObject<HTMLInputElement>;
  remainingBalance: number;
  isExtraValue: boolean;
}> = ({
  betInputValue: Config,
  minLimits,
  maxLimits,
  clearPrice,
  showFormatter,
  formatter,
  inputRef,
  remainingBalance,
  isExtraValue,
}) => {
  const tableLimitMods = useModifiers(styles, 'virtualKeypadContent', {
    tableMinLimit:
      parseFloat(Config) < minLimits ||
      parseFloat(Config) > maxLimits ||
      Config === '' ||
      Config === '.' ||
      parseFloat(Config) > remainingBalance ||
      isExtraValue,
  });

  const currencySymbol = useGameStateSlice.getCurrencyInfo()?.symbol;
  const showFormatInput = () => {
    if (showFormatter) {
      return Config + formatter;
    } else {
      return currencySymbol + Config;
    }
  };

  const checkLowVal = (val: number) => {
    if (showFormatter) {
      return val + formatter;
    } else {
      return currencySymbol + roundNumber(Number(val), 2).toString();
    }
  };

  return (
    <div className={styles.betAmountInput}>
      <input
        className={`${styles.virtualInput} ${styles.amount} ${tableLimitMods}`}
        type="text"
        ref={inputRef}
        value={Config === '' ? checkLowVal(0) : showFormatInput()}
        readOnly={true}
      />
      <div className={styles.deleteIcon} onClick={() => clearPrice()}></div>
    </div>
  );
};

const TableLimts: FC<{
  tableLimits: ILimit;
  currentLanguage: string;
  currency: string;
  hideCurrency: boolean;
  formatter: string;
}> = ({ tableLimits: Config, currentLanguage, currency, hideCurrency, formatter }) => {
  return (
    <div className={styles.tableLimits}>
      {CurrencyFormatter.format(Config.min, currentLanguage, {
        currency: currency,
        hideCurrency,
        avoidTrailingZero: true,
      }) + formatter}
      <span>&nbsp;-&nbsp;</span>
      {CurrencyFormatter.format(Config.max, currentLanguage, {
        currency: currency,
        hideCurrency,
        avoidTrailingZero: true,
      }) + formatter}
    </div>
  );
};
